"use client"

import Link from "next/link"
import { motion, useReducedMotion } from "motion/react"

import { Reveal, Stagger } from "@/components/motion"
import { EASE_OUT_QUINT } from "@/lib/motion-presets"

const G = "#16a34a" // primary green-600
const G_DARK = "#15803d" // green-700
const G_LIGHT = "#86efac" // green-300
const SOIL = "#92400e" // amber-800
const SUN = "#fbbf24" // amber-400
const WATER = "#3b82f6" // blue-500
const RED = "#ef4444"
const GRAY = "#9ca3af"
const INK = "#0f1d14"

interface Tool {
  name: string
  href: string
  featured?: boolean
  svg: React.ReactNode
}

// Order is data-driven from PostHog 90-day pageviews (re-pulled 2026-07-07).
// Curated to the ten most-viewed tools; the long tail (soil-test analyzer,
// aeration, tank-mix, sprinkler-cost, fertilizer-rate, and the newer
// calculators) lives on the /tools index, reachable via the "See all lawn
// care tools" link below. Top three get the "Most used" badge. Re-pull before
// the next homepage tweak — soil temp is heavily seasonal and the ranking
// shifts in spring/fall.
const TOOLS: Tool[] = [
  {
    name: "Soil temp",
    href: "/tools/soil-temperature",
    featured: true,
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <circle cx="29" cy="7" r="2.4" fill={SUN} />
        <path d="M29 2v2M29 10v1M24 7h2M32 7h2M25.6 3.6l1.2 1.2M31.2 9.2l1.2 1.2M25.6 10.4l1.2-1.2M31.2 4.8l1.2-1.2" stroke={SUN} strokeWidth="1" strokeLinecap="round" />
        <path d="M2 28h32" stroke={SOIL} strokeWidth="3.5" strokeLinecap="round" opacity="0.18" />
        <rect x="14" y="5" width="5" height="19" rx="2.5" stroke={INK} strokeWidth="1.6" fill="#fff" />
        <rect x="15.2" y="8" width="2.6" height="14" rx="1.3" fill="#fee2e2" />
        <rect x="15.2" y="15" width="2.6" height="7" rx="1.3" fill={RED} />
        <circle cx="16.5" cy="26" r="3.6" stroke={INK} strokeWidth="1.6" fill={RED} />
        <path d="M11.5 9h1.4M11.5 13h1.4M11.5 17h1.4M11.5 21h1.4" stroke={INK} strokeWidth="0.9" strokeLinecap="round" />
        <path d="M4 31c0-3 1-5 2-6M7 31c0-4 1-6 2-7M24 31c0-3 1-5 2-6M27 31c0-5 1-7 2-8M31 31c0-3 1-5 2-6" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: "Lawn size",
    href: "/tools/lawn-size-calculator",
    featured: true,
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M5 8l8-2 12 4 6-1v18l-7 2-11-3-8 1z" stroke={G_DARK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
        <path d="M5 8v18M13 6v25M25 10v22M31 9v18" stroke={G_LIGHT} strokeWidth="0.8" strokeDasharray="1.5 1.5" strokeLinecap="round" />
        <circle cx="18" cy="17" r="2.2" fill={G} />
        <circle cx="18" cy="17" r="3.6" stroke={G} strokeWidth="1.2" fill="none" opacity="0.5" />
        <path d="M6 30h24" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M6 28v4M30 28v4M12 29v2M18 29v2M24 29v2" stroke={INK} strokeWidth="1.2" strokeLinecap="round" />
        <text x="18" y="34.5" textAnchor="middle" fontSize="3.6" fontWeight="700" fill={INK} fontFamily="ui-sans-serif, system-ui">SQ FT</text>
      </svg>
    ),
  },
  {
    name: "Watering",
    href: "/tools/watering-schedule",
    featured: true,
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M9 13h11v9a3 3 0 01-3 3h-5a3 3 0 01-3-3z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
        <path d="M11.5 13v-2a3 3 0 013-3h2" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M20 16l5-2v3l-3 1" stroke={INK} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="#f0fdf4" />
        <path d="M22 18.5c-.5 1-1 2-1 3" stroke={WATER} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M25 19c-1 1-2 2-2 3.5c0 1 .8 1.5 1.5 1.5s1.5-.5 1.5-1.5c0-1.5-1-2.5-1-3.5z" fill={WATER} />
        <path d="M28 23c-1 1-1.5 2-1.5 3c0 .8.6 1.2 1.2 1.2s1.2-.4 1.2-1.2c0-1-.9-2-.9-3z" fill={WATER} opacity="0.7" />
        <path d="M19 31c0-3 1-5 2-7M22 31c0-2 1-4 2-5M26 31c0-3 1-5 2-7M30 31c0-2 1-4 2-5M16 31c0-2 0-3 0-4M13 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M2 32.5h32" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: "Herbicide timing",
    href: "/tools/herbicide-timing",
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <rect x="5" y="8" width="22" height="20" rx="2.4" stroke={INK} strokeWidth="1.6" fill="#fff" />
        <rect x="5" y="8" width="22" height="5" rx="2.4" fill={G} />
        <path d="M10 6v4M22 6v4" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M9 17h2M14 17h2M19 17h2M24 17h2M9 21h2M19 21h2M24 21h2" stroke={GRAY} strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="15" cy="21" r="2.8" fill={G} />
        <circle cx="15" cy="21" r="2.8" stroke={G_DARK} strokeWidth="0.8" fill="none" />
        <path d="M13.4 21l1.1 1.1 2.1-2.1" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="29" cy="29" r="3.6" fill="#fff" stroke={G_DARK} strokeWidth="1.4" />
        <path d="M29 27v2.4l1.6 1" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: "Fertilizer",
    href: "/tools/fertilizer-calculator",
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M11 11l1.5-3.5h11L25 11v17a2.5 2.5 0 01-2.5 2.5h-9A2.5 2.5 0 0111 28z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
        <rect x="13.5" y="14.5" width="9" height="6" rx="1" fill={G} />
        <text x="18" y="19" textAnchor="middle" fontSize="3.8" fontWeight="800" fill="#fff" fontFamily="ui-sans-serif, system-ui">NPK</text>
        <path d="M14 7.5l1-1h6l1 1" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="14" cy="25" r="0.9" fill={G} />
        <circle cx="17" cy="26" r="0.9" fill={G_LIGHT} />
        <circle cx="20" cy="25" r="0.9" fill={G} />
        <circle cx="22" cy="27" r="0.9" fill={G_LIGHT} />
        <circle cx="13" cy="28" r="0.7" fill={G_LIGHT} />
        <path d="M5 33c1-3 2-4 4-4M7 33c0-3 1-5 2-6M27 33c1-3 3-4 4-4M30 33c0-3 1-5 2-6" stroke={G} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M2 34h32" stroke={INK} strokeWidth="1.3" strokeLinecap="round" opacity="0.7" />
      </svg>
    ),
  },
  {
    name: "Mowing",
    href: "/tools/mowing-calculator",
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M28 6l-7 13" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M26 5h5" stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
        <rect x="6" y="18" width="17" height="8" rx="2" stroke={INK} strokeWidth="1.6" fill={G_LIGHT} />
        <path d="M9 18v-3M14 18v-3M19 18v-3" stroke={G_DARK} strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="11" cy="28" r="2.6" stroke={INK} strokeWidth="1.6" fill="#fff" />
        <circle cx="11" cy="28" r="0.9" fill={INK} />
        <circle cx="20" cy="28" r="2.6" stroke={INK} strokeWidth="1.6" fill="#fff" />
        <circle cx="20" cy="28" r="0.9" fill={INK} />
        <path d="M3 31c1-1 2-1 3 0" stroke={G} strokeWidth="1.3" strokeLinecap="round" fill="none" />
        <path d="M27 31c0-3 1-5 2-7M30 31c0-2 1-4 2-5M33 31c0-2 0-3 0-4" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M2 33h32" stroke={INK} strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: "Diagnose a problem",
    href: "/diagnose",
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        {/* Lawn with a problem patch under a magnifier */}
        <path d="M3 31c0-3 1-5 2-6M6 31c0-4 1-6 2-7M30 31c0-3 1-5 2-6M27 31c0-5 1-7 2-8" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M2 33.5h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="17" cy="16" r="8.5" stroke={INK} strokeWidth="1.6" fill="#fff" />
        <path d="M23.2 22.2l5 5" stroke={INK} strokeWidth="2.2" strokeLinecap="round" />
        <ellipse cx="15.5" cy="15" rx="3.4" ry="2.6" fill="#fde68a" />
        <ellipse cx="20" cy="18" rx="2" ry="1.5" fill="#fbbf24" opacity="0.7" />
        <path d="M12 20c1-1.5 2-2.5 3-3M19 11c1 .5 2 1.5 2.5 2.5" stroke={G} strokeWidth="1.3" strokeLinecap="round" />
        <path d="M28.5 9.5l1-2.5 1 2.5 2.5 1-2.5 1-1 2.5-1-2.5-2.5-1z" fill={RED} opacity="0.85" />
      </svg>
    ),
  },
  {
    name: "Grass comparison",
    href: "/tools/grass-comparison",
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        {/* Two grass swatches side by side with a VS badge */}
        <rect x="4" y="12" width="12" height="14" rx="2" fill="#dcfce7" stroke={INK} strokeWidth="1.5" />
        <path d="M6 12c0-2 0-3-.5-4M9 12c0-2.5 0-4-.5-5.5M12 12c0-2 0-3-.5-4M14.5 12c0-2.5 0-4-.5-5.5" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
        <rect x="20" y="12" width="12" height="14" rx="2" fill="#bbf7d0" stroke={INK} strokeWidth="1.5" />
        <path d="M22 12c0-2 0-3-.5-4M25 12c0-2.5 0-4-.5-5.5M28 12c0-2 0-3-.5-4M30.5 12c0-2.5 0-4-.5-5.5" stroke={G_DARK} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M18 8v20" stroke={INK} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="1.5 2" />
        <circle cx="18" cy="18" r="3.6" fill="#fff" stroke={INK} strokeWidth="1.3" />
        <text x="18" y="19.9" textAnchor="middle" fontSize="3.6" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">VS</text>
        <path d="M2 30h32" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: "Seeding",
    href: "/tools/seeding-calculator",
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M9 5l11-2.5 1.5 7-10 2z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#f0fdf4" />
        <path d="M11 11l9-2" stroke={G} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M14 13.5l-1.5 4M17 14l-1 4.5M19.5 13.5l1 5M22 13l2 4" stroke={SOIL} strokeWidth="1" strokeLinecap="round" opacity="0.5" />
        <circle cx="13" cy="20" r="0.9" fill={SOIL} />
        <circle cx="16" cy="22" r="0.9" fill={SOIL} />
        <circle cx="19" cy="20" r="0.9" fill={SOIL} />
        <circle cx="22" cy="23" r="0.9" fill={SOIL} />
        <circle cx="11" cy="24" r="0.7" fill={SOIL} />
        <circle cx="24" cy="20" r="0.7" fill={SOIL} />
        <path d="M14 30c0-3 0-5-1-7M17 30c0-2 0-3.5-.5-5M20 30c0-3 0-5 1-7M23 30c0-2 0-3.5.5-5M11 30c0-2 0-3 0-4M26 30c0-2 0-3 0-4" stroke={G} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M3 32h30" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    name: "Cost estimate",
    href: "/tools/lawn-cost-estimator",
    svg: (
      <svg width="38" height="38" viewBox="0 0 36 36" fill="none" aria-hidden="true">
        <path d="M9 4h18v26l-2.5-1.8L22 30l-2.5-1.8L17 30l-2.5-1.8L12 30l-3-1.8z" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" fill="#fff" />
        <path d="M13 9h10M13 12h10M13 15h7" stroke={GRAY} strokeWidth="1.3" strokeLinecap="round" />
        <rect x="11" y="19" width="14" height="6" rx="1" fill={G_LIGHT} />
        <text x="18" y="23.5" textAnchor="middle" fontSize="5.6" fontWeight="800" fill={G_DARK} fontFamily="ui-sans-serif, system-ui">$</text>
        <circle cx="29" cy="11" r="3.5" fill={SUN} stroke={INK} strokeWidth="1.2" />
        <text x="29" y="13" textAnchor="middle" fontSize="3.6" fontWeight="800" fill={INK} fontFamily="ui-sans-serif, system-ui">$</text>
      </svg>
    ),
  },
]

function FeaturedBadge() {
  const reduce = useReducedMotion()
  return (
    <motion.span
      className="absolute -top-2 right-3 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-green-600 text-white shadow-sm"
      initial={reduce ? false : { opacity: 0, scale: 0.85 }}
      whileInView={reduce ? undefined : { opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.3, ease: EASE_OUT_QUINT, delay: 0.18 }}
    >
      Most used
    </motion.span>
  )
}

export function ToolsChipStrip() {
  return (
    <section className="bg-green-50/40 border-t border-green-100 px-7 py-14">
      <div className="max-w-[1100px] mx-auto text-center">
        <Reveal>
          <h2 className="font-bold tracking-[-0.02em] text-gray-900 mb-2 text-[28px] md:text-4xl">
            Free calculators, tuned to your zip.
          </h2>
          <p className="text-sm text-gray-500 mb-7">
            Sorted by what lawn owners used most in the last 90 days.
          </p>
        </Reveal>
        <Stagger
          staggerChildren={0.04}
          delayChildren={0.05}
          className="grid grid-cols-2 md:grid-cols-5 gap-4"
        >
          {TOOLS.map((tool) => (
            <Stagger.Item key={tool.name}>
              <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.12 }}>
                <Link
                  href={tool.href}
                  className="group relative flex flex-col items-center px-3 pt-6 pb-5 rounded-2xl bg-white border border-gray-200 text-gray-900 transition-[transform,box-shadow,border-color] duration-150 hover:border-green-600 hover:-translate-y-0.5 hover:shadow-md"
                >
                  {tool.featured && <FeaturedBadge />}
                  <div className="w-[68px] h-[68px] rounded-full bg-green-50 flex items-center justify-center mb-3.5 transition-transform duration-200 ease-out group-hover:scale-[1.06]">
                    {tool.svg}
                  </div>
                  <div className="text-[15px] font-semibold leading-tight text-center">
                    {tool.name}
                  </div>
                </Link>
              </motion.div>
            </Stagger.Item>
          ))}
        </Stagger>
        <Reveal>
          <div className="mt-8">
            <Link
              href="/tools"
              className="group inline-flex items-center gap-2 rounded-full border border-green-200 bg-white px-6 py-3 text-sm font-semibold text-green-700 shadow-sm transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-0.5 hover:border-green-600 hover:shadow-md"
            >
              See all lawn care tools
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
